import {Injectable} from '@angular/core';
import {RequestParamModel} from '../models';
import {PaginationModel} from '../models';
import {BaseService} from '../services';
import {Observable, of, throwError} from 'rxjs';
import {catchError, map, shareReplay} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class CommonSearchService {

  /** See `indexSize()` — resolved once per session, then replayed. */
  private indexSize$?: Observable<number>;

  constructor(
    private baseService: BaseService
  ) {
  }

  /**
   * How many records the palette can actually search — the total across accounts,
   * contacts, sample requests, quotes and orders, for the empty-state hint.
   *
   * Read from `X-Pagination-Total-Count` on a keyword-less `common-search`, which is
   * the endpoint's own count of its corpus, so the hint cannot drift from what the
   * search will really look through. `pageSize = 1` because only the header is
   * wanted; the one row that comes back with it is discarded.
   *
   * `shareReplay` because the number is decorative and near-static: the palette is a
   * fresh component on every open, and without this every ⌘K would spend a COUNT
   * query on a sentence. It also means the count is fetched at most once per session,
   * so a record created afterwards is not reflected until reload — an acceptable
   * trade for a hint, and the reason this is not wired to anything that must be live.
   *
   * A failure resolves to 0 rather than propagating: the hint simply omits the clause
   * (see the template), so a count that cannot be fetched never breaks the palette.
   */
  indexSize(): Observable<number> {
    if (!this.indexSize$) {
      const requestParamModel = new RequestParamModel();
      requestParamModel.pageSize = 1;
      this.indexSize$ = this.getSearchResult(requestParamModel, true).pipe(
        map((result: PaginationModel<any>) => result.totalCount || 0),
        catchError(() => of(0)),
        shareReplay({bufferSize: 1, refCount: false})
      );
    }
    return this.indexSize$;
  }

  // getSearchResult(requestParamModel: RequestParamModel): Observable<any> {
  //   return this.baseService.get('common-search', requestParamModel)
  //     .pipe(map<any, any>(response => {
  //       return response.body.data;
  //     }, (err: any) => {
  //       return err;
  //     }));
  // }

  /**
   * @param skipLoader the command palette renders its own inline spinner, so it opts out of
   *                   the full-screen loader that would otherwise cover it on every keystroke.
   */
  getSearchResult(requestParamModel: RequestParamModel, skipLoader = false): Observable<any> {
    return this.baseService.get('common-search', requestParamModel, skipLoader)
      .pipe(map<any, any>(response => {
        const searchResults = new PaginationModel<any>();
        const headers = response.headers;
        searchResults.setHeaders(headers);
        searchResults.data = response.body.data as any;
        return searchResults;
         }),
      catchError(err => throwError(() => err))
  );
  }
}
